import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { familyAPI, storiesAPI } from '../services/api';
import './FamilyTree.css';

const FamilyTree = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [circle, setCircle] = useState(null);
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberStories, setMemberStories] = useState([]);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [circleRes, storiesRes] = await Promise.all([
        familyAPI.getOne(id),
        storiesAPI.getByFamily(id, {})
      ]);
      setCircle(circleRes.data.familyCircle);
      setStories(storiesRes.data.stories);
    } catch (error) {
      console.error('Error loading data:', error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const getStoriesForMember = (memberId) => {
    return stories.filter(s => 
      (s.storytellerId?._id || s.storytellerId) === memberId
    );
  };

  const handleMemberClick = (member) => {
    const memberId = member.userId?._id || member.userId;
    setSelectedMember(member);
    setMemberStories(getStoriesForMember(memberId));
  };

  const closePanel = () => {
    setSelectedMember(null);
    setMemberStories([]);
  };

  const getRelationshipColor = (relationship) => {
    const colors = {
      'Creator': '#8b5cf6',
      'Self': '#8b5cf6',
      'Spouse': '#ec4899',
      'Wife': '#ec4899',
      'Husband': '#ec4899',
      'Partner': '#ec4899',
      'Mother': '#f59e0b',
      'Father': '#f59e0b',
      'Parent': '#f59e0b',
      'Son': '#10b981',
      'Daughter': '#10b981',
      'Child': '#10b981',
      'Brother': '#3b82f6',
      'Sister': '#3b82f6',
      'Sibling': '#3b82f6',
      'Grandmother': '#f97316',
      'Grandfather': '#f97316',
      'Grandparent': '#f97316',
      'Grandson': '#06b6d4',
      'Granddaughter': '#06b6d4',
      'Grandchild': '#06b6d4',
      'Aunt': '#a855f7',
      'Uncle': '#a855f7',
      'Cousin': '#6366f1',
      'Niece': '#14b8a6',
      'Nephew': '#14b8a6',
    };
    return colors[relationship] || '#6b7280';
  };

  const getRelationshipIcon = (relationship) => {
    const icons = {
      'Creator': '👑',
      'Self': '👤',
      'Spouse': '💕',
      'Wife': '💕',
      'Husband': '💕',
      'Partner': '💕',
      'Mother': '👩',
      'Father': '👨',
      'Parent': '👪',
      'Son': '👦',
      'Daughter': '👧',
      'Child': '🧒',
      'Brother': '👦',
      'Sister': '👧',
      'Sibling': '🧑',
      'Grandmother': '👵',
      'Grandfather': '👴',
      'Grandparent': '👴',
      'Grandson': '👦',
      'Granddaughter': '👧',
      'Grandchild': '🧒',
      'Aunt': '👩',
      'Uncle': '👨',
      'Cousin': '🧑',
      'Niece': '👧',
      'Nephew': '👦',
    };
    return icons[relationship] || '👤';
  };

  // Group members by relationship type for tree layout
  const groupMembersByGeneration = () => {
    const groups = {
      grandparents: [],
      parents: [],
      self: [],
      siblings: [],
      spouse: [],
      children: [],
      grandchildren: [],
      extended: []
    };

    circle?.members?.forEach(member => {
      const rel = member.relationship?.toLowerCase() || '';
      
      if (rel.includes('grand') && (rel.includes('mother') || rel.includes('father') || rel.includes('parent'))) {
        groups.grandparents.push(member);
      } else if (rel.includes('mother') || rel.includes('father') || rel.includes('parent')) {
        groups.parents.push(member);
      } else if (rel.includes('creator') || rel.includes('self') || rel === '') {
        groups.self.push(member);
      } else if (rel.includes('brother') || rel.includes('sister') || rel.includes('sibling')) {
        groups.siblings.push(member);
      } else if (rel.includes('spouse') || rel.includes('wife') || rel.includes('husband') || rel.includes('partner')) {
        groups.spouse.push(member);
      } else if (rel.includes('son') || rel.includes('daughter') || rel.includes('child')) {
        if (rel.includes('grand')) {
          groups.grandchildren.push(member);
        } else {
          groups.children.push(member);
        }
      } else if (rel.includes('grandson') || rel.includes('granddaughter') || rel.includes('grandchild')) {
        groups.grandchildren.push(member);
      } else {
        groups.extended.push(member);
      }
    });

    return groups;
  };

  if (loading) {
    return (
      <div className="family-tree-loading">
        <div className="spinner"></div>
        <p>Loading family tree...</p>
      </div>
    );
  }

  const groups = groupMembersByGeneration();

  return (
    <div className="family-tree-page">
      <header className="tree-header">
        <button className="btn-back" onClick={() => navigate(`/family/${id}`)}>
          ← Back
        </button>
        <div className="tree-title">
          <h1>🌳 {circle?.name} Family Tree</h1>
          <p>{circle?.members?.length} members</p>
        </div>
      </header>

      <div className="tree-container">
        <div className="tree-visualization">
          {/* Grandparents Row */}
          {groups.grandparents.length > 0 && (
            <div className="tree-row grandparents-row">
              <div className="row-label">Grandparents</div>
              <div className="row-members">
                {groups.grandparents.map((member, idx) => (
                  <MemberNode 
                    key={idx} 
                    member={member} 
                    stories={getStoriesForMember(member.userId?._id || member.userId)}
                    onClick={() => handleMemberClick(member)}
                    getColor={getRelationshipColor}
                    getIcon={getRelationshipIcon}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Parents Row */}
          {groups.parents.length > 0 && (
            <div className="tree-row parents-row">
              <div className="row-label">Parents</div>
              <div className="row-members">
                {groups.parents.map((member, idx) => (
                  <MemberNode 
                    key={idx} 
                    member={member} 
                    stories={getStoriesForMember(member.userId?._id || member.userId)}
                    onClick={() => handleMemberClick(member)}
                    getColor={getRelationshipColor}
                    getIcon={getRelationshipIcon}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Self & Spouse & Siblings Row */}
          <div className="tree-row main-row">
            <div className="row-label">You & Siblings</div>
            <div className="row-members">
              {groups.siblings.map((member, idx) => (
                <MemberNode 
                  key={`sib-${idx}`} 
                  member={member} 
                  stories={getStoriesForMember(member.userId?._id || member.userId)}
                  onClick={() => handleMemberClick(member)}
                  getColor={getRelationshipColor}
                  getIcon={getRelationshipIcon}
                />
              ))}
              {groups.self.map((member, idx) => (
                <MemberNode 
                  key={`self-${idx}`} 
                  member={member} 
                  stories={getStoriesForMember(member.userId?._id || member.userId)}
                  onClick={() => handleMemberClick(member)}
                  getColor={getRelationshipColor}
                  getIcon={getRelationshipIcon}
                  isMain
                />
              ))}
              {groups.spouse.map((member, idx) => (
                <MemberNode 
                  key={`spouse-${idx}`} 
                  member={member} 
                  stories={getStoriesForMember(member.userId?._id || member.userId)}
                  onClick={() => handleMemberClick(member)}
                  getColor={getRelationshipColor}
                  getIcon={getRelationshipIcon}
                />
              ))}
            </div>
          </div>

          {/* Children Row */}
          {groups.children.length > 0 && (
            <div className="tree-row children-row">
              <div className="row-label">Children</div>
              <div className="row-members">
                {groups.children.map((member, idx) => (
                  <MemberNode 
                    key={idx} 
                    member={member} 
                    stories={getStoriesForMember(member.userId?._id || member.userId)}
                    onClick={() => handleMemberClick(member)}
                    getColor={getRelationshipColor}
                    getIcon={getRelationshipIcon}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Grandchildren Row */}
          {groups.grandchildren.length > 0 && (
            <div className="tree-row grandchildren-row">
              <div className="row-label">Grandchildren</div>
              <div className="row-members">
                {groups.grandchildren.map((member, idx) => (
                  <MemberNode 
                    key={idx} 
                    member={member} 
                    stories={getStoriesForMember(member.userId?._id || member.userId)}
                    onClick={() => handleMemberClick(member)}
                    getColor={getRelationshipColor}
                    getIcon={getRelationshipIcon}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Extended Family Row */}
          {groups.extended.length > 0 && (
            <div className="tree-row extended-row">
              <div className="row-label">Extended Family</div>
              <div className="row-members">
                {groups.extended.map((member, idx) => (
                  <MemberNode 
                    key={idx} 
                    member={member} 
                    stories={getStoriesForMember(member.userId?._id || member.userId)}
                    onClick={() => handleMemberClick(member)}
                    getColor={getRelationshipColor}
                    getIcon={getRelationshipIcon}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Member Detail Panel */}
        {selectedMember && (
          <div className="member-panel">
            <button className="panel-close" onClick={closePanel}>✕</button>
            
            <div className="panel-header">
              <div 
                className="panel-avatar"
                style={{ borderColor: getRelationshipColor(selectedMember.relationship) }}
              >
                {(selectedMember.userId?.fullName || selectedMember.userId?.displayName)?.charAt(0) || '?'}
              </div>
              <h2>{selectedMember.userId?.fullName || selectedMember.userId?.displayName || 'Unknown'}</h2>
              <span 
                className="panel-relationship"
                style={{ background: getRelationshipColor(selectedMember.relationship) }}
              >
                {getRelationshipIcon(selectedMember.relationship)} {selectedMember.relationship || 'Member'}
              </span>
            </div>

            <div className="panel-stats">
              <div className="stat">
                <span className="stat-value">{memberStories.length}</span>
                <span className="stat-label">Stories</span>
              </div>
              <div className="stat">
                <span className="stat-value">{selectedMember.role}</span>
                <span className="stat-label">Role</span>
              </div>
            </div>

            {memberStories.length > 0 ? (
              <div className="panel-stories">
                <h3>📖 Their Stories</h3>
                <div className="stories-mini-list">
                  {memberStories.map(story => (
                    <div 
                      key={story._id}
                      className="story-mini-card"
                      onClick={() => navigate(`/story/${story._id}`)}
                    >
                      <span className="story-mini-title">{story.title}</span>
                      <span className="story-mini-date">
                        {new Date(story.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="panel-no-stories">
                <p>No stories recorded yet</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Member Node Component
const MemberNode = ({ member, stories, onClick, getColor, getIcon, isMain }) => {
  const storyCount = stories.length;
  const relationship = member.relationship || 'Member';
  const color = getColor(relationship);
  const icon = getIcon(relationship);

  return (
    <div 
      className={`member-node ${isMain ? 'main-member' : ''}`}
      onClick={onClick}
      style={{ '--member-color': color }}
    >
      <div className="node-avatar">
        {(member.userId?.fullName || member.userId?.displayName)?.charAt(0) || '?'}
        {storyCount > 0 && (
          <span className="story-badge">{storyCount}</span>
        )}
      </div>
      <div className="node-name">{member.userId?.fullName || member.userId?.displayName || 'Unknown'}</div>
      <div className="node-relationship">
        <span className="rel-icon">{icon}</span>
        {relationship}
      </div>
    </div>
  );
};

export default FamilyTree;